import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useRouter } from '@/hooks';
import {
  useDefectsAreaByPlatformQuery,
  useDefectLegendsForPaintBlockTableQuery,
} from '@/__generated__/graphql';
import { Box } from '@mui/material';
import { TemporalAnalysisGraphs } from './TemporalAnalysisGraphs/TemporalAnalysisGraphs';
import { PageLoader } from '@/components/shared/PageLoader';
import { BreakdownValues } from './types';

type Props = {
  activeDeploymentIds: string[];
  setSelectedDeployments: Dispatch<SetStateAction<string[]>>;
  selectedDeployments: string[];
};

export const CoatingConditionGraph = ({
  activeDeploymentIds,
  setSelectedDeployments,
  selectedDeployments,
}: Props) => {
  const router = useRouter();
  const { platformId } = router.query;

  const { loading: loadingDefectsArea, data: defectsAreaPlatformData } =
    useDefectsAreaByPlatformQuery({
      variables: {
        platformId: platformId ? platformId.toString() : '',
      },
      skip: !platformId || platformId === '',
    });

  const { loading: loadingDefectLegends, data: defectLegendsData } =
    useDefectLegendsForPaintBlockTableQuery({
      variables: {
        structureId: activeDeploymentIds[0],
      },
      skip: !activeDeploymentIds[0],
    });

  const breakdownValues: BreakdownValues = useMemo(() => {
    const aggregatedConditions = Object.values(
      defectsAreaPlatformData?.defectsAreaByStructures || []
    )
      // based on selected deployments filter the data from defectsAreaPlatformData
      .filter(({ structureName }) => selectedDeployments.includes(structureName))
      // split the data into sub categories based on the defectLegendsData
      .reduce((accumulator: BreakdownValues, { structureName, totalArea }) => {
        defectLegendsData?.structure?.defectLegends?.forEach(
          ({ groupName, groupKey, name, color }) => {
            accumulator[groupKey] = accumulator[groupKey] || {
              values: [],
              colors: {},
              title: groupName,
            };

            let groupData = accumulator[groupKey].values.find(
              (entry) => entry.name === structureName
            );

            // Create a new groupData if it doesn't exist
            if (!groupData) {
              groupData = { name: structureName };
              accumulator[groupKey].values.push(groupData);
            }

            // Ensure groupData has the correct type and assign values
            groupData[name] = totalArea.find((area) => area.type === name)?.value || 0;

            accumulator[groupKey].colors[name] = color;
          }
        );

        return accumulator;
      }, {});

    return aggregatedConditions;
  }, [defectsAreaPlatformData, selectedDeployments, defectLegendsData]);

  const siteNames = useMemo(() => {
    if (!defectsAreaPlatformData) return [];
    return Object.values(defectsAreaPlatformData.defectsAreaByStructures).map(
      ({ structureName }) => structureName
    );
  }, [defectsAreaPlatformData]);

  useEffect(() => {
    if (siteNames.length > 0) {
      setSelectedDeployments(siteNames);
    }
  }, [siteNames, setSelectedDeployments]);

  return (
    <>
      {loadingDefectLegends || loadingDefectsArea ? (
        <Box sx={{ position: 'relative', height: '100%' }}>
          <PageLoader />
        </Box>
      ) : (
        <>
          {Object.values(breakdownValues).map((graph) => {
            return (
              <TemporalAnalysisGraphs
                key={graph.title}
                title={graph.title + ' - Overall platform'}
                data={graph.values}
                legendData={graph.colors}
              />
            );
          })}
        </>
      )}
    </>
  );
};
